import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { startIntroMusic, stopMusic } from '@/game/music';
import { resumeAudio, isMuted, toggleMute } from '@/game/audio';
import { setMusicMuted } from '@/game/music';
import { PATCH_NOTES } from '@/game/patchNotes';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/hooks/useAuth';
import UserStatus from '@/components/UserStatus';

const LandingPage: React.FC = () => {
  const { isGuest } = useAuth();
  const navigate = useNavigate();
  const [showHowTo, setShowHowTo] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const musicStarted = useRef(false);
  const [soundMuted, setSoundMuted] = useState(isMuted());

  const handleInteraction = useCallback(() => {
    if (!musicStarted.current) {
      musicStarted.current = true;
      resumeAudio();
      startIntroMusic();
    }
  }, []);

  const handleToggleSound = () => {
    toggleMute();
    setSoundMuted(isMuted());
  };

  const toggleSection = (section: 'howto' | 'about' | 'whatsnew') => {
    setShowHowTo(section === 'howto' ? !showHowTo : false);
    setShowAbout(section === 'about' ? !showAbout : false);
    setShowWhatsNew(section === 'whatsnew' ? !showWhatsNew : false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div
      className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-16 select-none overflow-y-auto relative"
      onClick={handleInteraction}
      onTouchStart={handleInteraction}
    >
      {/* Top bar */}
      <div className="absolute top-4 left-4 z-50">
        <button onClick={handleToggleSound}
          className="font-pixel text-lg px-3 py-2 bg-secondary/60 text-foreground hover:bg-secondary/80 rounded-sm border border-border transition-colors"
          title={soundMuted ? 'Sound Off' : 'Sound On'}>
          {soundMuted ? '🔇' : '🔊'}
        </button>
      </div>
      <div className="absolute top-4 right-4 z-50 flex gap-2 items-center">
        {isGuest ? (
          <button
            onClick={() => navigate('/auth')}
            className="font-pixel text-[10px] sm:text-xs px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 transition-all rounded-sm border border-primary/50"
          >
            👤 SIGN IN
          </button>
        ) : (
          <UserStatus />
        )}
      </div>
      {/* Title */}
      <div className="text-center mb-8">
        <h1 className="font-pixel text-4xl sm:text-6xl text-primary mb-4 tracking-wider leading-relaxed">
          PISTOL<br />DUEL
        </h1>
        <p className="font-retro text-xl sm:text-2xl text-muted-foreground">
          Settle your disputes the old-fashioned way.
        </p>
      </div>

      {/* Play button */}
      <button
        onClick={() => { stopMusic(); navigate('/play'); }}
        className="font-pixel text-base sm:text-lg px-12 py-5 bg-primary text-primary-foreground hover:bg-primary/90 transition-all rounded-sm border-2 border-primary/50 hover:border-primary active:scale-95 mb-8"
      >
        ⚔ PLAY
      </button>

      {/* Secondary buttons */}
      <div className="flex flex-wrap gap-4 mb-8 justify-center">
        <button
          onClick={() => navigate('/leaderboard')}
          className="font-pixel text-[10px] sm:text-xs px-6 py-3 bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-all rounded-sm border-2 border-border"
        >
          🏆 LEADERBOARD
        </button>
        <button
          onClick={() => toggleSection('howto')}
          className="font-pixel text-[10px] sm:text-xs px-6 py-3 bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-all rounded-sm border-2 border-border"
        >
          ? HOW TO PLAY
        </button>
        <button
          onClick={() => toggleSection('about')}
          className="font-pixel text-[10px] sm:text-xs px-6 py-3 bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-all rounded-sm border-2 border-border"
        >
          ★ ABOUT
        </button>
        <button
          onClick={() => toggleSection('whatsnew')}
          className="font-pixel text-[10px] sm:text-xs px-6 py-3 bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-all rounded-sm border-2 border-border"
        >
          ✦ WHAT'S NEW
        </button>
      </div>

      {/* How to play section */}
      {showHowTo && (
        <div className="max-w-md w-full bg-card/80 border-2 border-border rounded-sm p-6 mb-6 text-center">
          <h2 className="font-pixel text-sm text-primary mb-4">HOW IT WORKS</h2>
          <div className="font-retro text-lg text-foreground space-y-3">
            <p>Wait longer for better aim.</p>
            <p>But shoot before your opponent.</p>
            <p className="text-muted-foreground text-base mt-4">
              Draw too early and you might miss.<br />
              Wait too long and you might lose.
            </p>
            <p className="text-primary mt-4">Settle your dispute and duel.</p>
          </div>
        </div>
      )}

      {/* About section */}
      {showAbout && (
        <div className="max-w-md w-full bg-card/80 border-2 border-border rounded-sm p-6 mb-6 text-center">
          <h2 className="font-pixel text-sm text-primary mb-4">ABOUT</h2>
          <div className="font-retro text-lg text-foreground space-y-3">
            <p className="text-muted-foreground text-base">
              A small web experiment about settling disputes the old-fashioned way: with a duel.
            </p>
            <p className="mt-4">Created by</p>
            <p className="text-primary text-xl">Marcus Vinicius</p>
            <a
              href="https://instagram.com/marcus.vsousa"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-2 text-accent hover:text-accent/80 transition-colors"
            >
              @marcus.vsousa
            </a>
            <p className="text-muted-foreground text-sm mt-4">
              Built as an indie browser game experiment.<br />
              Thanks to everyone who tried the duel.
            </p>
          </div>
        </div>
      )}

      {/* What's New section — scrollable patch history */}
      {showWhatsNew && (
        <div className="max-w-md w-full bg-card/80 border-2 border-border rounded-sm p-6 mb-6 text-center">
          <h2 className="font-pixel text-sm text-primary mb-4">WHAT'S NEW</h2>
          <ScrollArea className="h-64">
            <div className="space-y-6 pr-3">
              {PATCH_NOTES.map(patch => (
                <div key={patch.version} className="text-left">
                  <p className="font-pixel text-[10px] text-muted-foreground mb-2">
                    Patch {patch.version} — {patch.date}
                  </p>
                  <div className="font-retro text-base text-foreground space-y-1">
                    {patch.changes.map((c, i) => (
                      <p key={i}>• {c}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

    </div>
  );
};

export default LandingPage;
